import React, { useState, useEffect, useContext } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import ComponentsUNames from 'constants/componentsUNames'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSquarePlus } from '@fortawesome/free-regular-svg-icons'
import Verdict from '../components/verdict'
import LocalStorage from 'utils/myLocalStorage'
import IcoMagnify from 'resources/images/app/amasis_icons/190100200.png'
import {
  createUseStyles,
  IconButton,
  Link,
  Pagination,
  Table,
  TableBody,
  TableHead,
  TableRow,
} from '@airbus/components-react/components'
import tools from 'utils/helpers'
import URL_APIS from 'utils/api/urlApis'
import LangContext from 'context/LangContext'
import useQuery from 'utils/hooks/useQuery'
import ResponsiveTableCell from 'components/responsiveTableCell'
import { Table as TableStyles } from '@airbus/elements/elements'
import clsx from 'clsx'
import config from 'constants/config'

const useStyles = createUseStyles(TableStyles, 'datatable')

const WidgetAircrafts = ({ selectedAircraft, pageUniqueName, handleAircraftChange, aircrafts, setAircrafts }) => {
  const baseUrl = process.env.PUBLIC_URL

  const { t } = useTranslation()
  const navigate = useNavigate()
  const classes = useStyles()

  const [searchParams] = useSearchParams()
  const jumpToPage = Number(searchParams.get('page')) || 0

  // Does the user own a license for viewing and creating TechLogs
  const hasUserTechlogLicense = eval('CONFIGAPP__LICACCESS["' + ComponentsUNames.CRM_LICACCESS + '"]')

  const { lang } = useContext(LangContext)

  const { fetchData: fetchAircrafts } = useQuery(URL_APIS.URL_FLEETPAN_AIRCRAFTS_GET)

  const [isLoadInProgressActive, setIsLoadInProgressActive] = useState(false)

  const [pagination, setPagination] = useState({
    nbItems: 0,
    pageSize: LocalStorage.getOrDefault(
      [
        LocalStorage.KEY_PREFS,
        ComponentsUNames.TABFLOT_STORE_NAME,
        ComponentsUNames.TABFLOT_WDG_AERONEFS_UNAME + '|' + ComponentsUNames.PROP_PAGE_SIZE,
      ],
      LocalStorage.VALUE_TYPE.INTEGER,
      config.PAGINATION_DEFAULT_PAGESIZE,
    ),
    pageNo: 0 || jumpToPage,
    nbPages: 0,
    choiceNbRows: [5, 10, 20, 50],
  })

  const canCreateTL = LocalStorage.getOrDefault(
    [LocalStorage.KEY_PERMISSIONS, ComponentsUNames.CRM_CREATE_PERMISSIONS],
    LocalStorage.VALUE_TYPE.BOOLEAN,
    false,
  )
  const canViewBdv = LocalStorage.getOrDefault(
    [LocalStorage.KEY_PERMISSIONS, ComponentsUNames.BDV_PERMISSIONS],
    LocalStorage.VALUE_TYPE.BOOLEAN,
    false,
  )

  const hasAcConfigPermissions = LocalStorage.getOrDefault(
    [LocalStorage.KEY_PERMISSIONS, ComponentsUNames.TABFLOT_AIRCRAFTS_CONFIGURATION_PERMISSIONS],
    LocalStorage.VALUE_TYPE.BOOLEAN,
    false,
  )

  /********
   * Synchronization of widget's data to keep track of them
   ********/

  const loadData = async () => {
    setIsLoadInProgressActive(true)
    const response = await fetchAircrafts({
      lang,
      acToFind: selectedAircraft || '',
      nbMonths: eval('CONFIGAPP__NBMONTHS_FOR_DATE_INTERVALS'),
      size: pagination.pageSize,
      page: jumpToPage || 0,
    })
    setIsLoadInProgressActive(false)
    setAircrafts(response?.data.content)

    setPagination({
      ...pagination,
      nbItems: response?.data.totalElements,
      nbPages: response?.data.totalPages,
      pageNo: response?.data.number,
    })
  }

  const handlePageChange = (page) => {
    navigate(baseUrl + '/fleetpan?page=' + encodeURIComponent(page), {
      preventScrollReset: true,
    })
  }

  useEffect(() => {
    loadData()
  }, [lang, pagination.pageSize, jumpToPage])

  const openLastTl = (avimmatr, date, lastTL) => {
    const urlToNavigateTo =
      baseUrl +
      '/fleetpan/' +
      encodeURIComponent(avimmatr) +
      '/' +
      encodeURIComponent(tools.convertDatePformatToYyyyMmJj(date)) +
      '/' +
      encodeURIComponent(lastTL)
    navigate(urlToNavigateTo)
  }

  const createNewTl = (avimmatr) => {
    const urlToNavigateTo = baseUrl + '/fleetpan/' + encodeURIComponent(avimmatr) + '/new'
    navigate(urlToNavigateTo)
  }

  // style={{ minWidth: '1200px', overflowX: 'scroll' }}

  return (
    <div>
      {aircrafts && JSON.stringify(aircrafts) !== '{}' && JSON.stringify(aircrafts) !== '[]' ? (
        <div>
          <Table>
            <colgroup>
              {/* Verdict */}
              <col style={{ width: '4%' }} />
              {/* A/C */}
              <col style={{ width: '6%' }} />
              {/* Escale */}
              <col style={{ width: '6%' }} />
              {/* Hangar */}
              <col style={{ width: '8%' }} />
              {/* Parking */}
              <col style={{ width: '9%' }} />
              {/* Dernier CRM */}
              <col style={{ width: '8%' }} />
              {/* + button */}
              <col style={{ width: '3%' }} />
              {/* Date */}
              <col style={{ width: '9%' }} />
              {/* Situation */}
              <col style={{ width: '12%' }} />
              {/* BDV ouvert */}
              <col style={{ width: '11%' }} />
              <col style={{ width: '4%' }} />
              {/* Configuration */}
              {hasAcConfigPermissions && <col style={{ width: '10%' }} />}
            </colgroup>
            <TableHead>
              <TableRow>
                <ResponsiveTableCell head />
                <ResponsiveTableCell head>{t('9500031')}</ResponsiveTableCell>
                <ResponsiveTableCell head>{t('9500032')}</ResponsiveTableCell>
                <ResponsiveTableCell head>{t('7003777')}</ResponsiveTableCell>
                <ResponsiveTableCell head>{t('9500108')}</ResponsiveTableCell>
                <ResponsiveTableCell head colSpan='2'>
                  {t('9500033')}
                </ResponsiveTableCell>
                <ResponsiveTableCell head>{t('9500034')}</ResponsiveTableCell>
                <ResponsiveTableCell head>{t('9500035')}</ResponsiveTableCell>
                <ResponsiveTableCell head colSpan='2'>
                  {t('9500040')}
                </ResponsiveTableCell>
                {hasAcConfigPermissions && <ResponsiveTableCell head>{t('9500036')}</ResponsiveTableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {aircrafts ? (
                aircrafts.map((d, i) => (
                  <TableRow
                    title={d.avimmatr}
                    key={'line_aircraft_' + i}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleAircraftChange(d.avimmatr)}
                    className={clsx({
                      [classes.row_selected]: d.avimmatr === selectedAircraft,
                    })}
                  >
                    <ResponsiveTableCell>
                      <Verdict value={Math.max(d.hil, d.pppr, d.workorder)} />
                    </ResponsiveTableCell>
                    <ResponsiveTableCell>{d.hasOwnProperty('avimmatr') ? d.avimmatr : '-'}</ResponsiveTableCell>
                    <ResponsiveTableCell>{d.hasOwnProperty('station') ? d.station : '-'}</ResponsiveTableCell>
                    <ResponsiveTableCell>{d.hasOwnProperty('hangar') ? d.hangar : '-'}</ResponsiveTableCell>
                    <ResponsiveTableCell>{d.hasOwnProperty('parking') ? d.parking : '-'}</ResponsiveTableCell>
                    {hasUserTechlogLicense === true ? (
                      <ResponsiveTableCell>
                        {d?.lastTL && (
                          <Link
                            size='small'
                            variant='underlined'
                            onClick={(e) => {
                              e.stopPropagation()
                              openLastTl(d.avimmatr, d.date, d.lastTL)
                            }}
                            style={{ padding: 'unset' }}
                          >
                            {d.lastTL}
                          </Link>
                        )}
                      </ResponsiveTableCell>
                    ) : (
                      <ResponsiveTableCell>{d.hasOwnProperty('lastTL') ? d.lastTL : '-'}</ResponsiveTableCell>
                    )}
                    {hasUserTechlogLicense === true ? (
                      <ResponsiveTableCell style={{ overflow: 'hidden', padding: 0 }}>
                        {canCreateTL && d.hasOwnProperty('lastTL') && (
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation()
                              createNewTl(d.avimmatr)
                            }}
                            variant='ghost'
                            size='xsmall'
                          >
                            <FontAwesomeIcon icon={faSquarePlus} />
                          </IconButton>
                        )}
                      </ResponsiveTableCell>
                    ) : (
                      <ResponsiveTableCell />
                    )}
                    <ResponsiveTableCell>{d.hasOwnProperty('date') ? d.date : '-'}</ResponsiveTableCell>
                    <ResponsiveTableCell>{d.hasOwnProperty('availability') ? d.availability : '-'}</ResponsiveTableCell>

                    <ResponsiveTableCell>
                      {d?.openedWP && canViewBdv ? (
                        <Link
                          size='small'
                          variant='underlined'
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate(`${baseUrl}/workpackage/${d.openedWP}?search=${encodeURIComponent(d.avimmatr)}`)
                          }}
                        >
                          {d.openedWP}
                        </Link>
                      ) : (
                        <span>{d?.openedWP || '-'}</span>
                      )}
                    </ResponsiveTableCell>
                    <ResponsiveTableCell>
                      {canViewBdv && d.hasManyOpenedWP ? (
                        <img
                          src={IcoMagnify}
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate(`${baseUrl}/workpackage/?search=${encodeURIComponent(d.avimmatr)}`)
                          }}
                          alt='several WP'
                        />
                      ) : (
                        ''
                      )}
                    </ResponsiveTableCell>
                    {hasAcConfigPermissions && (
                      <ResponsiveTableCell>
                        {d.hasOwnProperty('configuration') ? d.configuration : '-'}
                      </ResponsiveTableCell>
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow />
              )}
            </TableBody>
            <TableRow>
              <ResponsiveTableCell colSpan={hasAcConfigPermissions ? '12' : '11'}>
                <Pagination
                  count={pagination.nbItems}
                  page={pagination.pageNo + 1}
                  onPageChange={(e, newPage) => handlePageChange(newPage - 1)}
                  rowsPerPage={pagination.pageSize}
                  onRowsPerPageChange={(e, newRowPerPage) => {
                    LocalStorage.put(
                      [
                        LocalStorage.KEY_PREFS,
                        pageUniqueName,
                        ComponentsUNames.TABFLOT_WDG_AERONEFS_UNAME + '|PAGESIZE',
                      ],
                      newRowPerPage,
                    )
                    setPagination({ ...pagination, pageSize: parseInt(newRowPerPage), pageNo: 0 })
                  }}
                  rowsPerPageOptions={pagination.choiceNbRows}
                  showRowsPerPage
                  rowsPerPageLabel={t('entries_per_page')}
                />
              </ResponsiveTableCell>
            </TableRow>
          </Table>
        </div>
      ) : (
        <div style={{ paddingLeft: '1em' }}>{isLoadInProgressActive ? t('9500166') : t('9500055')}</div>
      )}
    </div>
  )
}

export default WidgetAircrafts
